import { Warning as WarningIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { Box, Button, Grid2, Typography, Skeleton, Tooltip } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { FC, useState } from "react";

import { IVehicleLogRowDetails } from ".";
import { AGDialog } from "../../components/AGDialog";
import { useNotification } from "../../components/FeedbackNotification/useNotification";
import { convertSnakeToTitleCase } from "../../utils/commonUtils";
import {
	usePreviewChallanDetailsQuery,
	useCreateChallansMutation,
	useRetryChallansMutation
} from "../../redux/reducers/alerts.reducer";
import { ICarouselOverlayDetails, IDimensions, useCreateImageURL } from "../../hooks/useCreateImageURL";

interface IGenerateChallanDialogProps {
	open: boolean;
	vehicleLogDetails: IVehicleLogRowDetails | null;
	onClose: (success: boolean, challanGenerated?: boolean, retryChallan?: boolean) => void;
}

const GenerateChallanDialog: FC<IGenerateChallanDialogProps> = (props) => {
	const { open, vehicleLogDetails, onClose } = props;
	const { notify } = useNotification();

	const { createImageURL, downloadCanvasRef } = useCreateImageURL();

	const [showLoader, setShowLoader] = useState<boolean>(false);

	// PREVIEW CHALLAN DETAILS
	const {
		data: challanDetails,
		isLoading: challanDetailsLoading,
		isFetching: challanDetailsFetching
	} = usePreviewChallanDetailsQuery(vehicleLogDetails?.id ?? 0, {
		skip: !vehicleLogDetails || !open
	});

	// CREATE CHALLANS
	const [createChallans] = useCreateChallansMutation();

	// RETRY CHALLANS
	const [retryChallans] = useRetryChallansMutation();

	if (!vehicleLogDetails) return null;

	// Parse location coordinates (assuming format like "lat,long" or JSON string)
	const parseLocationCoordinates = (coordinates: string | undefined): { lat: string; long: string } => {
		if (!coordinates) return { lat: "--", long: "--" };

		try {
			// Try parsing as JSON first
			const parsed = JSON.parse(coordinates);
			if (parsed.lat && parsed.long) {
				return { lat: String(parsed.lat), long: String(parsed.long) };
			}
		} catch {
			// If not JSON, try comma-separated
			const parts = coordinates.split(",");
			if (parts.length === 2) {
				return { lat: parts[0].trim(), long: parts[1].trim() };
			}
		}

		return { lat: coordinates, long: "--" };
	};

	const locationCoords = parseLocationCoordinates(vehicleLogDetails.location_coordinates);

	const handleGenerateChallan = async () => {
		if (!vehicleLogDetails?.id) {
			notify("Invalid event ID", "error");
			return;
		}

		setShowLoader(true);

		try {
			const [selectedImage1, selectedImage2] = vehicleLogDetails.vehicle_images.filter(
				(imageItem) => !imageItem.ignore
			);

			const overlayDetails: ICarouselOverlayDetails = {
				productName: "MTES",
				timestamp: vehicleLogDetails.timestamp,
				numberPlate: vehicleLogDetails.vehicle_number,
				speed: vehicleLogDetails.radar_speed ?? vehicleLogDetails.speed ?? "",
				location: vehicleLogDetails.location_name ?? "",
				officerName: vehicleLogDetails.officer ?? "",
				coordinates: vehicleLogDetails.location_coordinates ?? "",
				violations: vehicleLogDetails.violations
			};

			const imageDimensions: IDimensions = {
				height: vehicleLogDetails.image_height,
				width: vehicleLogDetails.image_width
			};

			const image1URL = await createImageURL({
				src: selectedImage1?.url ?? "",
				overlayDetails: overlayDetails,
				bbox: vehicleLogDetails.bbox,
				imageDimensions: imageDimensions,
				isFrame: selectedImage1?.evidence_type === "FRAME",
				violations: vehicleLogDetails.violations
			});

			const image2URL = await createImageURL({
				src: selectedImage2?.url ?? "",
				overlayDetails: overlayDetails,
				bbox: vehicleLogDetails.bbox,
				imageDimensions,
				isFrame: selectedImage2.evidence_type === "FRAME",
				violations: vehicleLogDetails.violations
			});

			const isRetry = vehicleLogDetails.retry_challan === true;
			const apiCall = isRetry ? retryChallans : createChallans;
			const response = await apiCall({
				event_id: vehicleLogDetails.id,
				image_1: image1URL.replace("data:image/jpeg;base64,", ""),
				image_2: image2URL.replace("data:image/jpeg;base64,", "")
			}).unwrap();

			if (response.success) {
				const { stats, challans } = response;
				if (stats.failed > 0) {
					notify(
						`${isRetry ? "Retried" : "Created"} ${stats.issued} out of ${stats.total} challans. ${
							stats.failed
						} failed.`,
						"warning"
					);
				} else {
					notify(
						`Successfully ${isRetry ? "retried" : "created"} ${stats.issued} challan${stats.issued > 1 ? "s" : ""}`,
						"success"
					);
				}

				const challanCreated = challans.length > 0;
				const retryChallan = challans.some((item) => item.status === "failed");

				onClose(true, challanCreated, retryChallan);
			} else {
				notify(response.message || `Failed to ${isRetry ? "retry" : "generate"} challan`, "error");
			}

			setShowLoader(false);
		} catch (error: any) {
			const errorMessage =
				error?.data?.error ||
				(error?.data?.event_id && Array.isArray(error.data.event_id) ? error.data.event_id[0] : null) ||
				`Failed to ${vehicleLogDetails.retry_challan ? "retry" : "generate"} challan`;
			notify(errorMessage, "error");

			setShowLoader(false);
		}
	};

	return (
		<AGDialog
			bgGrey
			open={open}
			onClose={() => onClose(false)}
			title="Challan Generation"
			color="primary"
			maxWidth="lg"
		>
			<Box sx={{ padding: "1rem 0" }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
					{challanDetailsLoading || challanDetailsFetching ? (
						<>
							{/* Violations Card Skeleton */}
							<Box className="event-log-details-card">
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, marginBottom: "0.5rem" }}>
									<Skeleton variant="text" width={150} height={28} />
								</Box>
								<Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 0.5 }}>
									{[...Array(3)].map((_, index) => (
										<Box
											key={index}
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 1,
												padding: "0.5rem 0.75rem",
												width: "fit-content"
											}}
										>
											<Skeleton variant="circular" width={24} height={24} />
											<Skeleton variant="text" width={120} height={20} />
											<Skeleton variant="circular" width={20} height={20} />
										</Box>
									))}
								</Box>
							</Box>

							{/* Vehicle & Event Details Card Skeleton */}
							<Box className="event-log-details-card">
								<Skeleton variant="text" width={200} height={28} sx={{ marginBottom: "1rem" }} />
								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={100} height={20} />
											<Skeleton variant="text" width={120} height={24} />
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={100} height={20} />
											<Skeleton variant="text" width={150} height={24} />
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={100} height={20} />
											<Skeleton variant="text" width={80} height={24} />
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={100} height={20} />
											<Skeleton variant="text" width={40} height={24} />
										</Box>
									</Grid2>
								</Grid2>
							</Box>

							{/* Location Details Card Skeleton */}
							<Box className="event-log-details-card">
								<Skeleton variant="text" width={150} height={28} sx={{ marginBottom: "1rem" }} />
								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={100} height={20} />
											<Skeleton variant="text" width={150} height={24} />
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={80} height={20} />
											<Skeleton variant="text" width={100} height={24} />
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={80} height={20} />
											<Skeleton variant="text" width={100} height={24} />
										</Box>
									</Grid2>
								</Grid2>
							</Box>

							{/* Officer Details Card Skeleton */}
							<Box className="event-log-details-card">
								<Skeleton variant="text" width={130} height={28} sx={{ marginBottom: "1rem" }} />
								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={100} height={20} />
											<Skeleton variant="text" width={120} height={24} />
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={60} height={20} />
											<Skeleton variant="text" width={100} height={24} />
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={50} height={20} />
											<Skeleton variant="text" width={120} height={24} />
										</Box>
									</Grid2>
								</Grid2>
							</Box>
						</>
					) : (
						<>
							{/* Violations Card */}
							<Box className="event-log-details-card">
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, marginBottom: "0.5rem" }}>
									<Typography variant="h6" fontWeight={600} color="primary">
										Violations ({challanDetails?.violations?.length || 0})
									</Typography>
								</Box>

								{challanDetails?.violations && challanDetails.violations.length > 0 ? (
									<Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 0.5 }}>
										{challanDetails.violations.map((violation, index) => (
											<Box
												key={index}
												sx={{
													display: "inline-flex",
													alignItems: "center",
													gap: 1,
													padding: "0.5rem 0.75rem",
													backgroundColor: "#fafafa",
													borderRadius: "4px",
													border: "1px solid #e0e0e0",
													transition: "all 0.2s ease",
													width: "fit-content",
													maxWidth: "100%",
													"&:hover": {
														backgroundColor: "#f5f5f5",
														borderColor: "primary.main"
													}
												}}
											>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														minWidth: "24px",
														width: "24px",
														height: "24px",
														borderRadius: "50%",
														backgroundColor: "error.light",
														color: "error.main",
														flexShrink: 0
													}}
												>
													<WarningIcon sx={{ fontSize: "14px" }} />
												</Box>

												<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
													<Typography
														variant="body2"
														fontWeight={500}
														color="text.primary"
														sx={{ fontSize: "0.875rem", whiteSpace: "nowrap" }}
													>
														{convertSnakeToTitleCase(violation.violation_name)}
													</Typography>
													<Tooltip
														arrow
														placement="top"
														title={
															violation.can_create_challan
																? violation.offence_name || ""
																: violation.error || violation.offence_name || ""
														}
													>
														<Box
															sx={{
																display: "inline-flex",
																alignItems: "center",
																justifyContent: "center",
																minWidth: "20px",
																width: "20px",
																height: "20px",
																borderRadius: "50%",
																backgroundColor: violation.can_create_challan ? "success.light" : "error.light",
																flexShrink: 0,
																cursor: "help"
															}}
														>
															{violation.can_create_challan ? (
																<CheckCircleIcon sx={{ fontSize: "14px", color: "success.main" }} />
															) : (
																<CancelIcon sx={{ fontSize: "14px", color: "error.main" }} />
															)}
														</Box>
													</Tooltip>
												</Box>
											</Box>
										))}
									</Box>
								) : (
									<Box
										sx={{
											padding: "1rem",
											textAlign: "center",
											backgroundColor: "#fafafa",
											borderRadius: "4px",
											border: "1px dashed #e0e0e0"
										}}
									>
										<Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
											No violations found
										</Typography>
									</Box>
								)}
							</Box>

							{/* Vehicle & Event Details Card */}
							<Box className="event-log-details-card">
								<Typography variant="h6" fontWeight={600} color="primary" sx={{ marginBottom: "1rem" }}>
									Vehicle & Event Details
								</Typography>

								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Number Plate
											</Typography>
											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.vehicle_number || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Date & Time
											</Typography>
											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.timestamp
													? moment(vehicleLogDetails.timestamp).format("DD/MM/YYYY HH:mm:ss")
													: "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Vehicle Speed
											</Typography>
											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.speed || vehicleLogDetails.radar_speed || "--"}
											</Typography>
										</Box>
									</Grid2>

									{/* <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Speed Limit
											</Typography>
											<Typography variant="body1" fontWeight={500}>
												--
											</Typography>
										</Box>
									</Grid2> */}
								</Grid2>
							</Box>

							{/* Location Details Card */}
							<Box className="event-log-details-card">
								<Typography variant="h6" fontWeight={600} color="primary" sx={{ marginBottom: "1rem" }}>
									Location Details
								</Typography>

								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												District
											</Typography>
											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.location_name || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Latitude
											</Typography>
											<Typography variant="body1" fontWeight={500}>
												{locationCoords.lat}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Longitude
											</Typography>
											<Typography variant="body1" fontWeight={500}>
												{locationCoords.long}
											</Typography>
										</Box>
									</Grid2>
								</Grid2>
							</Box>

							{/* Officer Details Card */}
							<Box className="event-log-details-card">
								<Typography variant="h6" fontWeight={600} color="primary" sx={{ marginBottom: "1rem" }}>
									Officer Details
								</Typography>

								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Officer Name
											</Typography>
											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.officer || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Device
											</Typography>
											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.device || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Site
											</Typography>
											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.site_name || vehicleLogDetails.camera || "--"}
											</Typography>
										</Box>
									</Grid2>
								</Grid2>
							</Box>
						</>
					)}

					{/* Action Buttons */}
					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
						<Button variant="outlined" color="inherit" onClick={() => onClose(false)}>
							Cancel
						</Button>

						<LoadingButton
							disableElevation
							variant="contained"
							color="primary"
							loading={showLoader}
							disabled={challanDetailsLoading || challanDetailsFetching || !challanDetails}
							onClick={handleGenerateChallan}
						>
							{vehicleLogDetails.retry_challan ? "Retry Challan" : "Generate Challan"}
						</LoadingButton>
					</Box>
				</Box>
			</Box>

			<canvas
				ref={downloadCanvasRef}
				style={{
					width: "100%",
					backgroundSize: "contain",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					position: "absolute",
					top: -99999,
					left: -99999,
					opacity: 0
				}}
			/>
		</AGDialog>
	);
};

export default GenerateChallanDialog;
